import React from "react";
import { motion } from "framer-motion";
import { testimonials } from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { Avatar, AvatarFallback } from "./ui/avatar";

// We divide the original testimonials into three columns.
// Since we only have 5 testimonials in mock.js, we will distribute them
// and then use the mapping multiplier (new Array(2)) to make them loop infinitely.
const firstColumn = testimonials.slice(0, 2);
const secondColumn = testimonials.slice(2, 4);
const thirdColumn = testimonials.slice(4, 5); // Just one here, so it will repeat more frequently

export const TestimonialsColumn = ({ className, testimonialsList, duration = 10 }) => {
  return (
    <div className={className}>
      <motion.div
        animate={{
          translateY: "-50%",
        }}
        transition={{
          duration: duration,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
        className="flex flex-col gap-6 pb-6"
      >
        {[...new Array(3)].map((_, index) => (
          <React.Fragment key={index}>
            {testimonialsList.map(({ text, avatar, name, role }, i) => (
              <div
                className="p-8 rounded-2xl border bg-white dark:bg-white/[0.02] border-slate-200 dark:border-white/[0.06] shadow-lg shadow-blue-100 dark:shadow-blue-500/5 max-w-sm w-full"
                key={i}
              >
                <div className="text-slate-600 dark:text-white/60 text-sm leading-relaxed mb-6">
                  "{text}"
                </div>
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10 bg-blue-100 dark:bg-blue-600/20 border border-blue-200 dark:border-blue-500/20">
                    <AvatarFallback className="bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-300 text-sm font-semibold">
                      {avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <div className="text-slate-900 dark:text-white text-sm font-semibold">
                      {name}
                    </div>
                    <div className="text-slate-400 dark:text-white/40 text-xs">
                      {role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  );
};

export const TestimonialsSection = () => {
  const [titleRef, titleVisible] = useScrollReveal();

  return (
    <section id="depoimentos" className="relative py-32 bg-slate-50 dark:bg-[#050E21] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-blue-500/20 to-transparent" />

      {/* Background accent */}
      <div
        className="absolute top-1/2 left-0 -translate-y-1/2 w-[400px] h-[400px] opacity-3 dark:opacity-10 pointer-events-none hidden dark:block"
        style={{
          background: "radial-gradient(circle, rgba(37,99,235,0.3) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={titleVisible ? { opacity: 1, y: 0 } : {}}
            className="inline-block text-blue-600 dark:text-blue-400 text-sm font-semibold tracking-widest uppercase mb-4"
          >
            Depoimentos
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={titleVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white"
          >
            O que dizem sobre
            <span className="bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
              {" "}
              nós
            </span>
          </motion.h2>
        </div>

        {/* Columns */}
        <div className="flex justify-center gap-6 mt-10 [mask-image:linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)] max-h-[600px] overflow-hidden">
          <TestimonialsColumn testimonialsList={firstColumn} duration={15} />
          <TestimonialsColumn
            testimonialsList={secondColumn}
            className="hidden sm:block"
            duration={19}
          />
          <TestimonialsColumn
            testimonialsList={thirdColumn}
            className="hidden lg:block"
            duration={17}
          />
        </div>
      </div>
    </section>
  );
};
